import React, { useEffect, useRef, useState } from 'react';
import { SwipeableHandlers, useSwipeable } from 'react-swipeable';
import { DialogProps } from '@mui/material/Dialog';

type InputProps = {
	onClose?: () => void;
	isMobile: boolean;
	isOpen: boolean;
	scroll?: DialogProps['scroll'];
	enableSwipeToClose?: boolean;
};

type ReturnProps = {
	handlers: SwipeableHandlers;
	swipeBlockRef: React.MutableRefObject<HTMLDivElement>;
	swipeFxRef: React.MutableRefObject<HTMLDivElement>;
	contentRef: React.MutableRefObject<HTMLDivElement>;
	paperRef: React.MutableRefObject<HTMLDivElement>;
};

const SWIPE_CLOSE_THRESHOLD = 70;

export const useSwipeDown = ({
	onClose,
	isMobile,
	isOpen,
	scroll,
	enableSwipeToClose = true
}: InputProps): ReturnProps => {
	const [isScrolled, setIsScrolled] = useState(false);
	const [delta, setDelta] = useState(0);
	const [swipeFlag, setSwipeFlag] = useState(onClose && isMobile);

	const isSwipeEnabled = Boolean(enableSwipeToClose && isMobile && isOpen && onClose);

	const paperRef = useRef(null);
	const swipeBlockRef = useRef(null);
	const swipeFxRef = useRef(null);
	const contentRef = useRef(null);

	const swipeableConfig = {
		delta: 30, // min distance(px) before a swipe starts. *See Notes*
		preventScrollOnSwipe: true, // prevents scroll during swipe (*See Details*)
		trackTouch: true, // track touch input
		trackMouse: true, // track mouse input
		rotationAngle: 0, // set a rotation angle
		swipeDuration: Infinity, // allowable duration of a swipe (ms). *See Notes*
		touchEventOptions: { passive: false } // options for touch listeners (*See Details*)
	};

	const setInitialStyles = () => {
		if (paperRef.current) {
			paperRef.current.style.transform = 'translateY(0)';
		}

		if (swipeFxRef.current) {
			swipeFxRef.current.style.transform = 'scale(1)';
		}

		setDelta(0);
	};

	const handleSetSwipeFlag = (target: Node) => {
		const startedInsideContent = !!target && !!contentRef.current?.contains(target);

		setSwipeFlag(!(startedInsideContent && isScrolled && onClose));
	};

	const handlers = useSwipeable({
		...swipeableConfig,

		onSwipedDown: () => {
			if (!swipeFlag || !isSwipeEnabled) {
				return;
			}

			if (delta > SWIPE_CLOSE_THRESHOLD) {
				onClose();
			}
		},

		onSwipeStart: data => {
			if (!isSwipeEnabled) {
				return;
			}

			handleSetSwipeFlag(data.event.target as Node);
		},

		onTouchStartOrOnMouseDown: data => {
			if (!isSwipeEnabled) {
				return;
			}

			handleSetSwipeFlag(data.event.target as Node);
		},

		onTouchEndOrOnMouseUp: () => {
			if (!swipeFlag || !isSwipeEnabled) {
				return;
			}

			if (delta < SWIPE_CLOSE_THRESHOLD) {
				setInitialStyles();
			}

			setSwipeFlag(!(isScrolled && onClose));
		},

		onSwiping: data => {
			if (!swipeFlag || !isSwipeEnabled) {
				return;
			}

			const deltaY = data.deltaY;

			setDelta(deltaY);

			if (deltaY > 0) {
				if (paperRef.current) {
					paperRef.current.style.transform = `translateY(${deltaY}px)`;
				}

				if (swipeFxRef.current) {
					swipeFxRef.current.style.transform =
						deltaY <= SWIPE_CLOSE_THRESHOLD ? `scale(${1 - deltaY / SWIPE_CLOSE_THRESHOLD})` : 'scale(0)';
				}
			}
		}
	});

	useEffect(() => {
		if (!isSwipeEnabled) {
			return undefined;
		}

		const onScroll = (e: Event) => {
			setIsScrolled((e.target as HTMLElement).scrollTop > 0);
		};

		if (contentRef.current && isOpen && onClose) {
			setInitialStyles();

			contentRef.current?.addEventListener('scroll', onScroll);

			handlers.ref(scroll === 'body' ? swipeBlockRef.current : paperRef.current);
		}

		return () => {
			contentRef.current?.removeEventListener('scroll', onScroll);

			handlers.ref(null);
		};
	}, [isOpen, isMobile, onClose, scroll]);

	return { handlers, contentRef, swipeBlockRef, swipeFxRef, paperRef };
};
